// OperatorFAQHelperPage.jsx
import React, {
  Fragment,
  useMemo,
  useState
} from "react";

import {
  AnimatePresence,
  motion,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform
} from "framer-motion";

import {
  Activity,
  ArrowRight,
  Binary,
  BookOpenCheck,
  Check,
  ChevronDown,
  CircleHelp,
  Copy,
  Database,
  Eye,
  FileCheck2,
  Fingerprint,
  Gauge,
  Layers3,
  LockKeyhole,
  Plug,
  RefreshCw,
  Scale,
  Search,
  Server,
  Settings2,
  ShieldCheck,
  Sparkles,
  WalletCards,
  X
} from "lucide-react";

import "./Styles.css";
import HeaderToMainPage from "../../Universal/Header/Index";
import Footer from "../../Universal/Footer/Index";
import { Link } from "react-router-dom";


/* ============================================================
   FAQ CONTENT
============================================================ */

const faqCategories = [
  {
    id: "fairness",
    index: "01",
    label: "Getting started",
    kicker: "FIT + FIRST LOOK",
    icon: CircleHelp,
    accent: "cyan",
    summary: "Decide whether Ethosino fits your business before discussing features or signing a contract.",
    questions: [
      { id: "fairness-01", q: "Who is Ethosino built for?", a: "Ethosino supplies B2B gaming software to businesses planning to operate a branded casino or sweepstakes-style product. It is not a consumer casino, a player-acquisition agency, or a shortcut to gambling authorization. The best fit is an operator with a defined audience, a realistic market-entry plan, and ownership of the commercial relationship with its players." },
      { id: "fairness-02", q: "Do I need an existing casino to evaluate the platform?", a: "No. A startup can assess the player demo and operator workflows before launching a brand. An existing operator can also evaluate Ethosino for a new product or brand. The first conversation should identify what your business already has—distribution, legal advice, payment partners, content—and what remains to be arranged." },
      { id: "fairness-03", q: "Can I try the games without creating a personal account?", a: "The public demo offers a demonstration-account entry point so visitors can explore supported experiences without registering a production player account. Access is still subject to the demo environment’s location and access checks. Demonstration balances and example data are not live operator funds." },
      { id: "fairness-04", q: "Why does the demo request geolocation?", a: "The demo uses location access to exercise the same kind of jurisdiction-aware entry flow operators need to evaluate. If location permission is declined or the environment cannot establish eligibility, access may be restricted. This is a deliberate product behavior, not a request to collect payment details." },
      { id: "fairness-05", q: "Can a prospect inspect the back office as well as the games?", a: "Yes, request an operator walkthrough that covers the administration experience, tenant configuration, player history, reporting, wallet workflows, and available review tools. Public demo access and privileged operator access are different; not every administrative action should be exposed to an anonymous visitor." },
      { id: "fairness-06", q: "What should I prepare for the first call?", a: "Bring your intended operating model, target jurisdictions, source of player traffic, desired launch catalog, payment and KYC assumptions, expected timeline, and an approximate budget. Ethosino can then identify which capabilities can be demonstrated, which integrations need scoping, and which obligations remain with the operator." },
    ],
    deepDive: {
      title: "Make the first demo a qualification exercise",
      body: "A useful product walkthrough should validate your business assumptions, not just showcase animations. The evaluation should end with a written gap list and a clear next step.",
      bullets: [
        "Identify target audience and permitted markets",
        "Inspect player and operator journeys",
        "Confirm launch dependencies and provider status",
        "Leave with a scoped follow-up, not a vague promise",
      ]
    }
  },
  {
    id: "execution",
    index: "02",
    label: "Launch process",
    kicker: "SCOPE + ACTIVATION",
    icon: Server,
    accent: "blue",
    summary: "Understand the path from an initial operator brief to a testable deployment and the checks that precede public launch.",
    questions: [
      { id: "execution-01", q: "What happens after we decide to proceed?", a: "The first deliverable is a scoped operator plan covering the brand, game package, expected integrations, roles, deployment context, and acceptance criteria. After commercial and technical prerequisites are agreed, Ethosino can provision the software environment and coordinate operator setup. Provisioning does not itself authorize public gameplay." },
      { id: "execution-02", q: "Which parts of tenant onboarding are automated?", a: "The tenant initialization workflow prepares the operator context and baseline configuration. A one-time administrator invitation allows the designated admin to activate the existing account and set credentials; the operator can then configure branding and permitted modules. Domain, payment, legal, and provider validation still need deployment-specific checks." },
      { id: "execution-03", q: "Can we start with an internal test or small pilot?", a: "That is the preferred evaluation approach where permitted: limit the audience and enabled capabilities, exercise critical workflows, then increase exposure only after issues are addressed. The scope, duration, allowable player activity, and success criteria should be written into the pilot arrangement." },
      { id: "execution-04", q: "What must pass testing before a live launch?", a: "At minimum, verify eligible access, registration and authentication, selected game flows, wallet accounting, payment callbacks, withdrawal review, operator permissions, support escalation, and relevant jurisdiction restrictions. Include failure cases such as timeouts and denied transactions, not only successful happy paths." },
      { id: "execution-05", q: "How quickly can a new operator go live?", a: "Software tenant creation and a legally authorized commercial launch are different milestones. Timing depends on the finalized catalog, payment-provider onboarding, KYC/AML arrangements, legal review, domain setup, and the issues found during acceptance testing. An actual launch date should follow those dependencies, not be promised universally." },
      { id: "execution-06", q: "Who signs off on launch readiness?", a: "Ethosino can demonstrate and validate the software scope; the operator should approve business rules, player-facing terms, provider relationships, compliance requirements, and test results. The agreement should name owners for each dependency and record unresolved items before enabling production traffic." },
    ],
    deepDive: {
      title: "Turn the launch plan into acceptance criteria",
      body: "A first release is ready when the agreed player and operator journeys work under realistic conditions, required partners are approved, and each remaining dependency has an owner.",
      bullets: [
        "Define an agreed scope and responsible contacts",
        "Test real success and failure scenarios",
        "Record outstanding production dependencies",
        "Make the launch decision explicitly",
      ]
    }
  },
  {
    id: "replay",
    index: "03",
    label: "Games & content",
    kicker: "CATALOG + EXPERIENCE",
    icon: Binary,
    accent: "green",
    summary: "Choose a launch-worthy game offering without assuming every title or external integration is available by default.",
    questions: [
      { id: "replay-01", q: "What game content can an operator start with?", a: "Ethosino has developed a catalog of original and familiar casino-style experiences, including number-based games, table games, and other interactive formats. The games enabled for a particular operator depend on the selected package, technical readiness, jurisdiction, and commercial agreement; the complete internal catalog is not an automatic entitlement." },
      { id: "replay-02", q: "Are the games developed in-house?", a: "A substantial portion of Ethosino’s game catalog and its underlying gameplay systems are first-party work. This matters because the platform can evolve presentation and mechanics directly rather than waiting on a third-party studio. Exact intellectual-property rights, game availability, and customization scope belong in the contract." },
      { id: "replay-03", q: "Can I choose which games appear in my casino?", a: "The operator can select from the titles and categories included in its licensed catalog and apply supported tenant-level visibility settings. A focused mix is often better for a pilot than activating every available game; choose titles based on the audience you can actually reach and measure." },
      { id: "replay-04", q: "What about poker tournaments, multiplayer tables, and immersive games?", a: "Ethosino has built additional formats beyond conventional solo games, including tournament and interactive table-game work. These experiences may involve realtime infrastructure, scheduling, concurrency, or additional QA, so availability and launch readiness should be demonstrated for the exact titles requested rather than inferred from a marketing screenshot." },
      { id: "replay-05", q: "Can third-party studios or content providers be connected?", a: "Potentially, but an external catalog should be treated as its own integration and commercial workstream. Provider approval, API requirements, commercial terms, permitted markets, and content certification may affect availability. A relationship or planned integration is not the same as a production-ready catalog." },
      { id: "replay-06", q: "How are game integrity and disputed rounds handled?", a: "Supported first-party games have game-specific historical inspection and verification pathways, which can include deterministic replay or event reconstruction depending on the mechanics. Ethosino’s Fairness & Verification page explains the methods and limitations in detail; the operator should request a live example for the games it plans to offer." },
    ],
    deepDive: {
      title: "Select a catalog with a purpose",
      body: "The launch package should be an explicit list of titles with named rules, supported devices, availability status, applicable restrictions, and operator acceptance checks—not an indefinite promise of “all games.”",
      bullets: [
        "Agree on an exact launch-title list",
        "Identify advanced or realtime titles requiring QA",
        "Separate first-party and external licensing",
        "Verify play and settlement before launch",
      ]
    }
  },
  {
    id: "tenant",
    index: "04",
    label: "Brand & player experience",
    kicker: "OWN THE CUSTOMER JOURNEY",
    icon: Layers3,
    accent: "violet",
    summary: "Understand how an operator shapes the storefront and player relationship without commissioning a separate frontend.",
    questions: [
      { id: "tenant-01", q: "Can the casino run under our own brand and domain?", a: "Yes. The operator configuration supports branded presentation and domain association, subject to normal domain ownership, DNS, and deployment steps. Ethosino maintains the underlying application; the business selects its public-facing identity and approved content." },
      { id: "tenant-02", q: "How much of the storefront can we change?", a: "Supported settings include themes, visual assets, homepage sections, navigation, and configurable copy. The precise boundaries of self-service editing should be demonstrated in the operator admin; deeper structural changes may be separate development work." },
      { id: "tenant-03", q: "Can we tailor promotions and retention experiences?", a: "Ethosino includes promotion, rewards, notifications, and player-engagement surfaces that can be configured within the enabled product scope. Your acquisition and retention strategy remains the operator’s responsibility; software features are tools, not a substitute for obtaining players." },
      { id: "tenant-04", q: "Will customers know they are using Ethosino?", a: "The intended model is an operator-branded player experience backed by Ethosino software. Contractual attribution, disclosure requirements, and applicable consumer notices should be settled in the operator agreement and legal review rather than assumed." },
      { id: "tenant-05", q: "Can we bring an existing player base?", a: "A migration may be possible with approved mappings for identity, consent, balances, and history, but it is not a one-click promise. Data availability, privacy obligations, duplicate-account handling, and financial reconciliation need to be assessed before imports are quoted." },
      { id: "tenant-06", q: "Who owns player data and the customer relationship?", a: "The operator-facing experience and commercial relationship are intended to belong to the operator. However, legal ownership, permitted data use, portability, retention, and deletion must be defined in the executed agreement and applicable privacy documentation. Do not assume full export rights or exclusivity without those terms." },
    ],
    deepDive: {
      title: "Brand control is not the same as marketing distribution",
      body: "An operator can choose its presentation and customer journey while still needing to earn traffic, answer player questions, and maintain its own customer commitments.",
      bullets: [
        "Confirm editable content and domain responsibilities",
        "Plan acquisition and retention independently",
        "Document migration or portability requirements",
        "Review customer-facing disclosures and data terms",
      ]
    }
  },
  {
    id: "risk",
    index: "05",
    label: "Payments & wallets",
    kicker: "PROCESSING + RECONCILIATION",
    icon: WalletCards,
    accent: "amber",
    summary: "Separate software money movement from real payment processing, banking relationships, and operator financial obligations.",
    questions: [
      { id: "risk-01", q: "Does Ethosino act as a bank or payment processor?", a: "No. Ethosino provides wallet, transaction, and operator-administration software. Accepting and paying out real value requires suitable providers and contractual arrangements for the operator’s model and jurisdictions. Provider approval should never be assumed from the existence of a payment interface." },
      { id: "risk-02", q: "Which payment gateways are already available?", a: "Gateway suitability depends on the operator’s model, geographic footprint, compliance requirements, and a working integration. Discuss any preferred provider before finalizing scope; planned partnerships or development-stage connectors should not be presented as universally available processing." },
      { id: "risk-03", q: "How are deposits and withdrawals handled?", a: "The platform provides application workflows and transaction context for incoming funds, balance changes, and payout review. Actual funding and disbursement depend on the connected provider, required identity checks, holds, risk approvals, and settlement policies. Test successful, declined, duplicate, and delayed callbacks before launch." },
      { id: "risk-04", q: "Can the operator reconcile player balances and game results?", a: "The operator-facing systems include wallet and transaction history, game references, and financial reporting surfaces. The purpose is to trace a balance change to a recognizable event, then compare platform records with provider settlements; the exact accounting and export fields should be confirmed for the deployment." },
      { id: "risk-05", q: "Are sweepstakes entries and cryptocurrency balances supported in the same way?", a: "The allowable value model depends on the product being launched and the relevant legal and provider requirements. Virtual or promotional balances, redemption rules, and crypto-related workflows require explicit configuration and legal review; they should not be treated as interchangeable with cash deposits." },
      { id: "risk-06", q: "Are processing fees, chargebacks, and reserves included in Ethosino pricing?", a: "Not automatically. Processor rates, rolling reserves, chargebacks, conversion costs, banking fees, and payout costs can be separate obligations. Ask for an itemized cost schedule and clarify who bears each fee or liability before signing." },
    ],
    deepDive: {
      title: "Follow the funds from player request to provider settlement",
      body: "A working wallet screen is only one step. Before launch, both sides should be able to explain the provider relationship, transaction reconciliation, payout review, and treatment of failed or reversed transactions.",
      bullets: [
        "Name the payment and payout providers",
        "Test callbacks, retries and reversals",
        "Assign reconciliation and dispute ownership",
        "Price processing fees separately",
      ]
    }
  },
  {
    id: "audit",
    index: "06",
    label: "Operations & support",
    kicker: "DAILY WORK + ESCALATION",
    icon: Activity,
    accent: "pink",
    summary: "Understand the work your team must perform once people begin using the casino.",
    questions: [
      { id: "audit-01", q: "What will my team manage in the operator dashboard?", a: "Depending on the licensed modules, the operator dashboard provides views for player accounts, game activity, balances and withdrawals, rewards, operational reporting, support, risk review, and configuration. A guided tour should trace a realistic workday rather than just show each navigation item." },
      { id: "audit-02", q: "Who answers players when something goes wrong?", a: "The operator should determine who owns front-line player support, hours of coverage, identity verification, goodwill decisions, and escalation. Ethosino offers support-related tools and can address software-level issues under the agreed service arrangement, but your player-service obligations do not transfer automatically." },
      { id: "audit-03", q: "How are disputed withdrawals or transactions reviewed?", a: "Authorized staff should be able to inspect the player context, relevant transaction references, and available operational history before escalating or deciding an action. Final payout authority and escalation paths depend on roles, provider rules, and the operator’s documented procedures." },
      { id: "audit-04", q: "What reporting should we use in the first month?", a: "Focus on player acquisition and activation, first-session completion, game usage, wagering and revenue measures, withdrawal aging, failed payments, and support response time. Ethosino exposes operational analytics, but a new operator still needs to agree on metric definitions and how they will be used." },
      { id: "audit-05", q: "Can alerts and activity histories help us investigate incidents?", a: "Available risk queues, access events, account activity, transaction history, and audit records provide starting points for review. Alerting effectiveness and retention vary by configuration; ask to test a concrete case such as a suspicious login, a payout delay, or an operator policy change." },
      { id: "audit-06", q: "What does the AI Operator Copilot contribute?", a: "The AI-assisted back-office experience is intended to summarize permitted operating data and help staff identify questions worth investigating. It is not a payment approver, fraud judge, game-outcome controller, or replacement for an accountable operator decision. Confirm the enabled scope and use illustrative demo answers only as examples." },
    ],
    deepDive: {
      title: "Assign people to the work—not just permissions to accounts",
      body: "Even with a connected back office, someone must own reconciliations, customer replies, payout exceptions, promotions, and incident decisions. A pilot should identify those owners.",
      bullets: [
        "Map recurring daily and weekly operator duties",
        "Define support hours and escalation contacts",
        "Agree on metrics and report definitions",
        "Test a real exception from intake to resolution",
      ]
    }
  },
  {
    id: "governance",
    index: "07",
    label: "Compliance & markets",
    kicker: "LEGAL + RESPONSIBILITY",
    icon: Scale,
    accent: "cyan",
    summary: "Clarify what the software can enforce and what the operator must establish through counsel and external partners.",
    questions: [
      { id: "governance-01", q: "Does licensing Ethosino authorize us to run a sweepstakes casino?", a: "No. Software access is not a gambling license or legal opinion. Operators must determine a lawful structure for each intended market and satisfy the applicable promotion, gaming, consumer-protection, and other obligations before accepting real players." },
      { id: "governance-02", q: "Will Ethosino provide a legal opinion for our business?", a: "Legal opinions and jurisdictional analysis should be obtained from qualified counsel engaged for the operator’s actual model. Ethosino can discuss platform capabilities and relevant configuration requirements, but do not treat general product descriptions as legal advice or a substitute for that work." },
      { id: "governance-03", q: "Does the platform include KYC and AML checks?", a: "Ethosino has onboarding and integration surfaces for identity and compliance workflows, but the screening provider, approval requirements, data handling, and ongoing review obligations must be chosen and validated for the deployment. A generic checkbox is not proof that a production KYC or AML program has been established." },
      { id: "governance-04", q: "How do geographic restrictions affect access?", a: "A tenant can be configured with jurisdiction and location restrictions, including response paths when location is unavailable or network signals are suspicious. The legal allowlist must come from the operator’s approved operating posture. See the Risk & Exposure page for control behavior rather than relying on this FAQ as a technical specification." },
      { id: "governance-05", q: "Who sets responsible-gaming and player-protection policies?", a: "The operator must select appropriate rules and operational practices with its advisors and team. Ethosino provides relevant account-state and enforcement tooling where enabled, but the operator still needs policies, training, escalation procedures, and ongoing oversight." },
      { id: "governance-06", q: "Can the international demo be used as proof a brand can operate worldwide?", a: "No. A demonstration access policy is not a production jurisdiction policy. The isolated demo can be configured for wider evaluation while a real operator must implement the restrictions and approvals applicable to the actual business." },
    ],
    deepDive: {
      title: "Make compliance a launch input, not a final checkbox",
      body: "Before a production timeline is promised, the operator should understand its permitted markets, provider relationships, player-protection obligations, and which workflows need external approval.",
      bullets: [
        "Obtain operator-specific legal advice",
        "Select and qualify identity/payment partners",
        "Translate approved markets into access rules",
        "Approve operating policies and staff procedures",
      ]
    }
  },
  {
    id: "performance",
    index: "08",
    label: "Hosting & reliability",
    kicker: "OPERATIONS + CAPACITY",
    icon: Database,
    accent: "green",
    summary: "Evaluate infrastructure expectations, release practices, and the service levels that belong in your deployment scope.",
    questions: [
      { id: "performance-01", q: "Do operators need to host and maintain their own application?", a: "Ethosino is designed around a maintained shared platform with tenant-specific configuration. The precise hosting topology and responsibilities depend on the selected commercial arrangement; custom or dedicated deployment needs must be scoped separately." },
      { id: "performance-02", q: "What technologies does the platform use?", a: "The current architecture uses cloud hosting and delivery services alongside Node-based application services, MongoDB persistence, realtime messaging, Redis, and background-job processing. A public stack list is not a security audit or a substitute for reviewing the exact production design for your deployment." },
      { id: "performance-03", q: "Can you guarantee a particular number of concurrent players?", a: "Capacity must be supported by load testing under the requested game mix, expected concurrency, realtime usage, and infrastructure topology. A universal concurrency number or uptime percentage should not be promised without supporting tests and a contractual service objective." },
      { id: "performance-04", q: "How are updates delivered to multiple brands?", a: "Shared platform development allows product fixes and improvements to be released without independently maintaining a code fork per operator. Rollout timing, change windows, backward compatibility, and any operator-specific testing requirements should be defined for production use." },
      { id: "performance-05", q: "What happens if a realtime or background service fails?", a: "Recovery behavior depends on the affected subsystem and deployment configuration. Operators should review how incomplete rounds, pending transactions, scheduled tournaments, notifications, and job retries are detected and reconciled; these scenarios belong in acceptance and incident testing." },
      { id: "performance-06", q: "What backup, disaster recovery, and support commitments are included?", a: "The answer must be specific to the contracted hosting and support plan. Ask for retention, backup, restoration, monitoring, incident escalation, response targets, and recovery assumptions in writing. Do not infer a dedicated SLA from a marketing tier label." },
    ],
    deepDive: {
      title: "Specify reliability in terms of tested failure scenarios",
      body: "A meaningful service agreement should cover not only normal traffic but what gets recorded, recovered, or escalated when a dependency fails.",
      bullets: [
        "Agree on capacity test assumptions",
        "Define release and maintenance expectations",
        "Test interrupted rounds and payment events",
        "Document support and recovery commitments",
      ]
    }
  },
  {
    id: "security",
    index: "09",
    label: "Security & information",
    kicker: "ACCESS + DILIGENCE",
    icon: LockKeyhole,
    accent: "red",
    summary: "Discuss how operator information is accessed, reviewed, shared, and protected without making unsupported certification claims.",
    questions: [
      { id: "security-01", q: "Can one operator see another operator’s customer records?", a: "The tenant-aware model is designed to restrict access according to authenticated operator and role context. This is logical application-level separation, not an automatic claim that every brand runs on separate physical hardware. Cross-tenant authorization should be tested during diligence." },
      { id: "security-02", q: "Who can change settings or approve sensitive actions?", a: "Operator roles and authorized workflows determine who may view or change the relevant records. A useful security review walks through an owner, support agent, compliance reviewer, and restricted account to confirm the permissions actually implemented for the deployment." },
      { id: "security-03", q: "Is the platform independently certified or security audited?", a: "Do not assume a particular certification, penetration-test result, or regulator approval unless Ethosino supplies the corresponding current evidence. Request the available test scope, findings, remediation status, and any contractual security requirements for your environment." },
      { id: "security-04", q: "Can we obtain technical documents for our engineering team?", a: "Yes, an operator can request appropriate architecture and integration material for technical diligence. Sensitive information may require a controlled review process or agreement. Public FAQs intentionally avoid publishing credentials, active secrets, or defensive bypass details." },
      { id: "security-05", q: "What happens to our data if we stop using Ethosino?", a: "Data access, return or export formats, retention, deletion, and transition support should be negotiated in the contract. The presence of analytics and audit pages does not guarantee unrestricted access to every raw system record or a turnkey migration." },
      { id: "security-06", q: "How should privacy and identity records be handled?", a: "The operator and its selected vendors need a documented privacy and access model, including the purpose of collection, consent or other lawful basis where required, retention, data-subject requests, and incident response. Ethosino’s software controls can support this, but a legal privacy review remains necessary." },
    ],
    deepDive: {
      title: "Ask for specific evidence, not reassuring labels",
      body: "Operator security diligence should verify implemented role boundaries, documented access to sensitive data, known test scope, and contractual incident obligations rather than assuming enterprise claims imply formal certification.",
      bullets: [
        "Test role and cross-tenant access boundaries",
        "Review security evidence actually available",
        "Define privacy, incident and data-exit terms",
        "Keep privileged implementation material controlled",
      ]
    }
  },
  {
    id: "integration",
    index: "10",
    label: "Pricing & agreements",
    kicker: "FEES + CONTRACT",
    icon: Plug,
    accent: "violet",
    summary: "Understand what is negotiable, what costs are outside Ethosino, and what must appear in an operator agreement.",
    questions: [
      { id: "integration-01", q: "How is Ethosino priced?", a: "The current pricing-page model describes a quoted monthly software license, included game package, optional additional-title licensing, and negotiated revenue participation. Starter through Enterprise describe different scopes. The written proposal—not an illustrative web widget—determines your actual commitments." },
      { id: "integration-02", q: "How does the revenue-share percentage work?", a: "The pricing page currently describes a 15–30% negotiated range. Before signing, define the exact percentage and revenue base, treatment of winnings and promotional credits, adjustments, chargebacks, report access, statement timing, and payment dates. The calculator illustrates arithmetic; it does not define contractual GGR or net revenue." },
      { id: "integration-03", q: "What is the price for extra first-party games?", a: "The current default shown on the pricing page is $500 per additional title per month per tenant when a requested game falls outside the included package. The contract should list the included titles, add-ons, effective dates, and whether negotiated exceptions apply." },
      { id: "integration-04", q: "Is there a free or discounted pilot?", a: "An introductory pilot may be discussed for a qualified operator, but price, duration, revenue participation, included capabilities, and any conversion terms must be confirmed in an offer. Do not assume a zero-fee pilot from a general marketing statement when another page lists standard paid terms." },
      { id: "integration-05", q: "What costs are not included in the Ethosino license?", a: "Legal review, licensing or registration costs, payment processing, identity verification, outside games, banking relationships, marketing, player support staffing, and some hosting or bespoke integration work may be separate. Request an itemized division of responsibilities and estimated third-party obligations." },
      { id: "integration-06", q: "What should we verify before signing?", a: "Ask for a statement of work with the exact game package, integrations, intended markets, tenant and brand count, pilot or subscription period, fee definitions, support levels, deliverables, acceptance criteria, security and data terms, and termination/transition provisions. Any investor or partner representations should be documented separately from platform licensing." },
    ],
    deepDive: {
      title: "Require a quote that can be operated, not just signed",
      body: "A good proposal makes the commercial scope auditable: you can tell what is included, what activates an extra charge, who owns the external dependencies, and what happens at renewal or exit.",
      bullets: [
        "Itemize software, add-ons, and provider costs",
        "Define the revenue base and reporting method",
        "Specify pilot conversion and acceptance criteria",
        "Document support, data, and exit obligations",
      ]
    }
  },
];



const heroSignals = [
  ["EVALUATION", "PRODUCT DEMO", CircleHelp],
  ["LAUNCH", "ACCEPTANCE PLAN", Server],
  ["CONTENT", "GAME SCOPE", Layers3],
  ["FINANCIAL", "PROVIDER STATUS", WalletCards],
  ["AGREEMENT", "WRITTEN TERMS", FileCheck2]
];


/* ============================================================
   MOTION
============================================================ */

const Reveal = ({
  children,
  className = "",
  direction = "up",
  delay = 0
}) => {
  const reduceMotion =
    useReducedMotion();

  const starts = {
    left: {
      x: -120,
      y: 0,
      rotate: -1.7
    },
    right: {
      x: 120,
      y: 0,
      rotate: 1.7
    },
    up: {
      x: 0,
      y: 75,
      rotate: 0
    },
    topLeft: {
      x: -150,
      y: -105,
      rotate: -2.6
    },
    topRight: {
      x: 150,
      y: -105,
      rotate: 2.6
    },
    bottomLeft: {
      x: -150,
      y: 110,
      rotate: -2.6
    },
    bottomRight: {
      x: 150,
      y: 110,
      rotate: 2.6
    }
  };

  const start =
    starts[direction] ||
    starts.up;

  return (
    <motion.div
      className={
        className
      }
      initial={
        reduceMotion
          ? false
          : {
              opacity:
                0,
              ...start,
              scale:
                0.95,
              filter:
                "blur(10px)"
            }
      }
      whileInView={{
        opacity:
          1,
        x:
          0,
        y:
          0,
        rotate:
          0,
        scale:
          1,
        filter:
          "blur(0px)"
      }}
      viewport={{
        once:
          true,
        amount:
          0.12
      }}
      transition={{
        duration:
          reduceMotion
            ? 0
            : 0.84,
        delay,
        ease: [
          0.16,
          1,
          0.3,
          1
        ]
      }}
    >
      {
        children
      }
    </motion.div>
  );
};


const Spotlight = ({
  children,
  className = ""
}) => {
  const handlePointerMove =
    event => {
      const rect =
        event.currentTarget.getBoundingClientRect();

      event.currentTarget.style.setProperty(
        "--ef10-mx",
        `${event.clientX - rect.left}px`
      );

      event.currentTarget.style.setProperty(
        "--ef10-my",
        `${event.clientY - rect.top}px`
      );
    };

  return (
    <div
      className={`eth-faq-v10-spotlight ${className}`}
      onPointerMove={
        handlePointerMove
      }
    >
      <div className="eth-faq-v10-spotlight-glow" />
      {
        children
      }
    </div>
  );
};


/* ============================================================
   HERO CONSOLE
============================================================ */

const DiligenceConsole = ({
  reduceMotion
}) => {
  return (
    <div className="eth-faq-v10-console">

      <div className="eth-faq-v10-console-grid" />

      <div className="eth-faq-v10-console-scan" />


      <div className="eth-faq-v10-console-top">

        <div>
          <span className="eth-faq-v10-live-dot" />

          OPERATOR QUESTIONS / GUIDE
        </div>

        <b>
          EVALUATION + DECISIONS
        </b>

      </div>


      <div className="eth-faq-v10-console-field">

        <motion.div
          className="eth-faq-v10-console-ring eth-faq-v10-console-ring--outer"
          animate={
            reduceMotion
              ? {}
              : {
                  rotate:
                    360
                }
          }
          transition={{
            duration:
              34,
            repeat:
              Infinity,
            ease:
              "linear"
          }}
        />

        <motion.div
          className="eth-faq-v10-console-ring eth-faq-v10-console-ring--middle"
          animate={
            reduceMotion
              ? {}
              : {
                  rotate:
                    -360
                }
          }
          transition={{
            duration:
              24,
            repeat:
              Infinity,
            ease:
              "linear"
          }}
        />


        <div className="eth-faq-v10-console-core">

          <span className="eth-faq-v10-console-wave" />

          <CircleHelp
            size={29}
            strokeWidth={1.6}
          />

          <small>
            ETHOSINO
          </small>

          <strong>
            OPERATOR FAQ
          </strong>

          <b>
            PRACTICAL ANSWERS
          </b>

        </div>


        {
          heroSignals.map(
            (
              signal,
              index
            ) => {
              const Icon =
                signal[2];

              return (
                <motion.div
                  key={
                    signal[0]
                  }
                  className={`eth-faq-v10-console-node eth-faq-v10-console-node--${index + 1}`}
                  animate={
                    reduceMotion
                      ? {}
                      : {
                          y: [
                            0,
                            index %
                            2 ===
                            0
                              ? -8
                              : 8,
                            0
                          ],
                          x: [
                            0,
                            index %
                            3 ===
                            0
                              ? 5
                              : -5,
                            0
                          ]
                        }
                  }
                  transition={{
                    duration:
                      4.6 +
                      index *
                      0.28,
                    repeat:
                      Infinity,
                    ease:
                      "easeInOut"
                  }}
                >
                  <Icon
                    size={15}
                  />

                  <div>
                    <span>
                      {
                        signal[0]
                      }
                    </span>

                    <strong>
                      {
                        signal[1]
                      }
                    </strong>
                  </div>
                </motion.div>
              );
            }
          )
        }


        <span className="eth-faq-v10-console-packet eth-faq-v10-console-packet--one" />

        <span className="eth-faq-v10-console-packet eth-faq-v10-console-packet--two" />

        <span className="eth-faq-v10-console-packet eth-faq-v10-console-packet--three" />

      </div>


      <div className="eth-faq-v10-console-bottom">

        <div>
          <span>
            PUBLIC GUIDE
          </span>

          <strong>
            BUYER QUESTIONS
          </strong>
        </div>

        <ArrowRight
          size={16}
        />

        <div>
          <span>
            SCOPED REVIEW
          </span>

          <strong>
            DEPLOYMENT DETAILS
          </strong>
        </div>

        <b>
          ASK WHAT APPLIES
        </b>

      </div>

    </div>
  );
};


/* ============================================================
   FAQ ITEM
============================================================ */

const FAQItem = ({
  item,
  category,
  index,
  isOpen,
  onToggle,
  reduceMotion
}) => {
  const Icon =
    category.icon;

  return (
    <motion.article
      className={[
        "eth-faq-v10-question",
        `eth-faq-v10-question--${category.accent}`,
        isOpen
          ? "is-open"
          : ""
      ].join(
        " "
      )}
      layout={
        !reduceMotion
      }
    >

      <button
        type="button"
        className="eth-faq-v10-question-trigger"
        onClick={
          onToggle
        }
        aria-expanded={
          isOpen
        }
      >

        <span className="eth-faq-v10-question-index">
          {
            String(
              index +
              1
            ).padStart(
              2,
              "0"
            )
          }
        </span>


        <span className="eth-faq-v10-question-icon">
          <Icon
            size={17}
          />
        </span>


        <span className="eth-faq-v10-question-copy">

          <small>
            {
              category.label
            }
          </small>

          <strong>
            {
              item.q
            }
          </strong>

        </span>


        <motion.span
          className="eth-faq-v10-question-chevron"
          animate={{
            rotate:
              isOpen
                ? 180
                : 0
          }}
        >
          <ChevronDown
            size={18}
          />
        </motion.span>

      </button>


      <AnimatePresence
        initial={false}
      >
        {
          isOpen
            ? (
              <motion.div
                className="eth-faq-v10-question-answer"
                initial={{
                  height:
                    0,
                  opacity:
                    0
                }}
                animate={{
                  height:
                    "auto",
                  opacity:
                    1
                }}
                exit={{
                  height:
                    0,
                  opacity:
                    0
                }}
                transition={{
                  duration:
                    reduceMotion
                      ? 0
                      : 0.28
                }}
              >
                <div className="eth-faq-v10-question-answer-inner">

                  <div className="eth-faq-v10-answer-rail">
                    <span />

                    ANSWER
                  </div>

                  <p>
                    {
                      item.a
                    }
                  </p>

                </div>
              </motion.div>
            )
            : null
        }
      </AnimatePresence>

    </motion.article>
  );
};


/* ============================================================
   PAGE
============================================================ */

const OperatorFAQHelperPage = () => {
  const reduceMotion =
    useReducedMotion();

  const [
    query,
    setQuery
  ] =
    useState(
      ""
    );

  const [
    activeCategory,
    setActiveCategory
  ] =
    useState(
      "fairness"
    );

  const [
    openQuestion,
    setOpenQuestion
  ] =
    useState(
      "fairness-01"
    );

  const [
    copied,
    setCopied
  ] =
    useState(
      false
    );


  const {
    scrollYProgress
  } =
    useScroll();


  const progress =
    useSpring(
      scrollYProgress,
      {
        stiffness:
          120,
        damping:
          28,
        restDelta:
          0.001
      }
    );


  const heroY =
    useTransform(
      scrollYProgress,
      [
        0,
        0.16
      ],
      [
        0,
        reduceMotion
          ? 0
          : 72
      ]
    );


  const heroOpacity =
    useTransform(
      scrollYProgress,
      [
        0,
        0.13
      ],
      [
        1,
        0.77
      ]
    );


  const totalQuestions =
    useMemo(
      () =>
        faqCategories.reduce(
          (
            total,
            category
          ) =>
            total +
            category.questions.length,
          0
        ),
      []
    );


  const filteredGroups =
    useMemo(
      () => {
        const normalized =
          query
            .trim()
            .toLowerCase();

        return faqCategories
          .filter(
            category =>
              activeCategory === "all" ||
              category.id === activeCategory
          )
          .map(
            category => ({
              ...category,
              questions:
                category.questions.filter(
                  item =>
                    !normalized ||
                    item.q
                      .toLowerCase()
                      .includes(
                        normalized
                      ) ||
                    item.a
                      .toLowerCase()
                      .includes(
                        normalized
                      ) ||
                    category.label
                      .toLowerCase()
                      .includes(
                        normalized
                      ) ||
                    category.kicker
                      .toLowerCase()
                      .includes(
                        normalized
                      )
                )
            })
          )
          .filter(
            category =>
              category.questions.length >
              0
          );
      },
      [
        query,
        activeCategory
      ]
    );


  const resultCount =
    useMemo(
      () =>
        filteredGroups.reduce(
          (
            total,
            category
          ) =>
            total +
            category.questions.length,
          0
        ),
      [
        filteredGroups
      ]
    );


  const tickerItems = useMemo(() => [
    "EVALUATE WITH A DEMO",
    "CONFIRM YOUR GAME CATALOG",
    "QUALIFY PAYMENT PROVIDERS",
    "AGREE ON PILOT CRITERIA",
    "CLARIFY DATA OWNERSHIP",
    "ITEMIZE THIRD-PARTY COSTS",
    "DEFINE SUPPORT RESPONSIBILITY",
    "PUT COMMERCIAL TERMS IN WRITING"
  ], []);


  const copyDiligencePrompt =
    async () => {
      try {
        await navigator.clipboard.writeText(
          "For my market and launch model, show what is ready to demonstrate, what requires an external provider, what we must supply, and what each item would cost."
        );

        setCopied(
          true
        );

        window.setTimeout(
          () =>
            setCopied(
              false
            ),
          1400
        );
      } catch {
        setCopied(
          false
        );
      }
    };


  const scrollToExplorer =
    () => {
      document
        .getElementById(
          "faq-explorer"
        )
        ?.scrollIntoView({
          behavior:
            "smooth",
          block:
            "start"
        });
    };


  return (
    <Fragment>

      <motion.div
        className="eth-faq-v10-scroll-progress"
        style={{
          scaleX:
            progress
        }}
      />


      <HeaderToMainPage />


      <main
        className="eth-faq-v10-page eth-faq-v10-readable"
        id="ethosino-operator-faq-v10"
      >

        {/* ====================================================
            ATMOSPHERE
        ==================================================== */}

        <div
          className="eth-faq-v10-atmosphere"
          aria-hidden="true"
        >
          <div className="eth-faq-v10-atmosphere-grid" />

          <div className="eth-faq-v10-atmosphere-noise" />

          <div className="eth-faq-v10-orb eth-faq-v10-orb--cyan" />

          <div className="eth-faq-v10-orb eth-faq-v10-orb--green" />

          <div className="eth-faq-v10-orb eth-faq-v10-orb--violet" />

          <div className="eth-faq-v10-orb eth-faq-v10-orb--pink" />
        </div>


        {/* ====================================================
            HERO
        ==================================================== */}

        <section style={{ marginTop: 50 }} className="eth-faq-v10-hero">

          <motion.div
            className="eth-faq-v10-hero-parallax"
            style={{
              y:
                heroY,
              opacity:
                heroOpacity
            }}
          >

            <div className="eth-faq-v10-shell eth-faq-v10-hero-grid">

              <div className="eth-faq-v10-hero-copy">

                <motion.div
                  className="eth-faq-v10-system-badge"
                  initial={
                    reduceMotion
                      ? false
                      : {
                          opacity:
                            0,
                          x:
                            -38
                        }
                  }
                  animate={{
                    opacity:
                      1,
                    x:
                      0
                  }}
                  transition={{
                    duration:
                      0.58
                  }}
                >
                  <span className="eth-faq-v10-live-dot" />

                  BUYER QUESTIONS

                  <i />

                  OPERATOR FAQ
                </motion.div>


                <motion.h1
                  initial={
                    reduceMotion
                      ? false
                      : {
                          opacity:
                            0,
                          x:
                            -110,
                          y:
                            44,
                          rotate:
                            -2
                        }
                  }
                  animate={{
                    opacity:
                      1,
                    x:
                      0,
                    y:
                      0,
                    rotate:
                      0
                  }}
                  transition={{
                    duration:
                      0.86,
                    delay:
                      0.04,
                    ease: [
                      0.16,
                      1,
                      0.3,
                      1
                    ]
                  }}
                >
                  Understand the offer.

                  <span>
                    Decide with the details.
                  </span>
                </motion.h1>


                <motion.p
                  className="eth-faq-v10-hero-lead"
                  initial={
                    reduceMotion
                      ? false
                      : {
                          opacity:
                            0,
                          y:
                            30
                        }
                  }
                  animate={{
                    opacity:
                      1,
                    y:
                      0
                  }}
                  transition={{
                    duration:
                      0.72,
                    delay:
                      0.14
                  }}
                >
                  Use these answers to evaluate Ethosino as an operator: what you can test, what a pilot includes, how payments and games are scoped, who handles day-to-day operations, and what belongs in a written agreement. For deeper engineering details, each dedicated product page covers its own system.
                </motion.p>


                <motion.div
                  className="eth-faq-v10-hero-thesis"
                  initial={
                    reduceMotion
                      ? false
                      : {
                          opacity:
                            0,
                          x:
                            -46
                        }
                  }
                  animate={{
                    opacity:
                      1,
                    x:
                      0
                  }}
                  transition={{
                    duration:
                      0.67,
                    delay:
                      0.24
                  }}
                >
                  <BookOpenCheck
                    size={18}
                  />

                  <span>
                    A useful sales conversation should end with{" "} <strong>an agreed scope, clear responsibilities and testable next steps</strong>{" "} — not assumptions about launch readiness.
                  </span>
                </motion.div>


                <motion.div
                  className="eth-faq-v10-hero-actions"
                  initial={
                    reduceMotion
                      ? false
                      : {
                          opacity:
                            0,
                          y:
                            24
                        }
                  }
                  animate={{
                    opacity:
                      1,
                    y:
                      0
                  }}
                  transition={{
                    duration:
                      0.62,
                    delay:
                      0.31
                  }}
                >

                  <button
                    type="button"
                    onClick={
                      scrollToExplorer
                    }
                    className="eth-faq-v10-btn eth-faq-v10-btn--primary"
                  >
                    Search the FAQ

                    <Search
                      size={17}
                    />
                  </button>


                  <Link
                    to="/book-demo"
                    className="eth-faq-v10-btn eth-faq-v10-btn--ghost"
                  >
                    Walk through it live
                  </Link>

                </motion.div>


                <div className="eth-faq-v10-hero-proofline">

                  <span>
                    <Fingerprint
                      size={14}
                    />
                    evaluation
                  </span>

                  <span>
                    <Server
                      size={14}
                    />
                    launch
                  </span>

                  <span>
                    <Layers3
                      size={14}
                    />
                    branding
                  </span>

                  <span>
                    <FileCheck2
                      size={14}
                    />
                    operations
                  </span>

                  <span>
                    <LockKeyhole
                      size={14}
                    />
                    agreements
                  </span>

                </div>

              </div>


              <motion.div
                className="eth-faq-v10-hero-visual"
                initial={
                  reduceMotion
                    ? false
                    : {
                        opacity:
                          0,
                        x:
                          120,
                        y:
                          -62,
                        rotate:
                          2.5,
                        scale:
                          0.94
                      }
                }
                animate={{
                  opacity:
                    1,
                  x:
                    0,
                  y:
                    0,
                  rotate:
                    0,
                  scale:
                    1
                }}
                transition={{
                  duration:
                    0.96,
                  delay:
                    0.12,
                  ease: [
                    0.16,
                    1,
                    0.3,
                    1
                  ]
                }}
              >

                <DiligenceConsole
                  reduceMotion={
                    reduceMotion
                  }
                />


                <motion.div
                  className="eth-faq-v10-float eth-faq-v10-float--one"
                  animate={
                    reduceMotion
                      ? {}
                      : {
                          y: [
                            0,
                            -12,
                            0
                          ],
                          rotate: [
                            -1,
                            1,
                            -1
                          ]
                        }
                  }
                  transition={{
                    duration:
                      5.6,
                    repeat:
                      Infinity,
                    ease:
                      "easeInOut"
                  }}
                >
                  <Search
                    size={15}
                  />

                  <div>
                    <span>
                      SEARCHABLE GUIDE
                    </span>

                    <strong>
                      {totalQuestions} QUESTIONS
                    </strong>
                  </div>
                </motion.div>


                <motion.div
                  className="eth-faq-v10-float eth-faq-v10-float--two"
                  animate={
                    reduceMotion
                      ? {}
                      : {
                          y: [
                            0,
                            11,
                            0
                          ],
                          rotate: [
                            1,
                            -1,
                            1
                          ]
                        }
                  }
                  transition={{
                    duration:
                      6.4,
                    repeat:
                      Infinity,
                    ease:
                      "easeInOut"
                  }}
                >
                  <ShieldCheck
                    size={15}
                  />

                  <div>
                    <span>
                      OPERATOR GUIDANCE
                    </span>

                    <strong>
                      BUYER-FACING
                    </strong>
                  </div>
                </motion.div>

              </motion.div>

            </div>

          </motion.div>


          <div className="eth-faq-v10-ticker">
            <div className="eth-faq-v10-ticker-track">

              {
                [
                  ...tickerItems,
                  ...tickerItems
                ].map(
                  (
                    item,
                    index
                  ) => (
                    <React.Fragment
                      key={`${item}-${index}`}
                    >
                      <span>
                        {
                          item
                        }
                      </span>

                      <i>
                        ◆
                      </i>
                    </React.Fragment>
                  )
                )
              }

            </div>
          </div>

        </section>


        {/* ====================================================
            SUBNAV
        ==================================================== */}

        <nav className="eth-faq-v10-subnav">

          <div className="eth-faq-v10-shell eth-faq-v10-subnav-inner">

            <div className="eth-faq-v10-subnav-brand">
              <span />

              FAQ TOPICS
            </div>


            <div className="eth-faq-v10-subnav-links">

              <a href="#faq-explorer">
                Explorer
              </a>

              <a href="#faq-proof">
                Review path
              </a>

              <a href="#faq-disclosure">
                Documents
              </a>

              <a href="#faq-operator">
                Responsibilities
              </a>

              <Link
                className="eth-faq-v10-subnav-cta"
                to="/request-documentation"
              >
                Technical brief
              </Link>

            </div>

          </div>

        </nav>


        {/* ====================================================
            HOW TO USE
        ==================================================== */}

        <section className="eth-faq-v10-section">

          <div className="eth-faq-v10-shell">

            <Reveal
              direction="topLeft"
              className="eth-faq-v10-section-head eth-faq-v10-section-head--split"
            >

              <div>
                <span className="eth-faq-v10-kicker">
                  FIND THE RIGHT ANSWER
                </span>

                <h2>
                  One business decision. <span>Several teams involved.</span>
                </h2>
              </div>

              <p>
                Founders need a realistic path to launch. Operations teams need to understand daily responsibilities. Engineers need to evaluate integrations and reliability, while legal and finance teams need clear boundaries. Start with the perspective that matters to your decision.
              </p>

            </Reveal>


            <div className="eth-faq-v10-review-grid">

              {
                [
                  {
                    icon:
                      Layers3,
                    label:
                      "FOUNDER",
                    title:
                      "What does our launch actually require?",
                    body:
                      "Start with fit, launch steps, the intended catalog, and market prerequisites."
                  },
                  {
                    icon:
                      Server,
                    label:
                      "PRODUCT & ENGINEERING",
                    title:
                      "Which integrations need proving?",
                    body:
                      "Check game readiness, payment events, hosting assumptions, and data access."
                  },
                  {
                    icon:
                      FileCheck2,
                    label:
                      "OPERATIONS",
                    title:
                      "Who handles the work after launch?",
                    body:
                      "Review player service, withdrawal handling, financial reporting, and incident workflows."
                  },
                  {
                    icon:
                      LockKeyhole,
                    label:
                      "LEGAL & FINANCE",
                    title:
                      "What must be written into the deal?",
                    body:
                      "Confirm approved markets, external fees, data rights, support and contract terms."
                  }
                ].map(
                  (
                    card,
                    index
                  ) => {
                    const Icon =
                      card.icon;

                    const directions = [
                      "topLeft",
                      "topRight",
                      "bottomLeft",
                      "bottomRight"
                    ];

                    return (
                      <Reveal
                        key={
                          card.label
                        }
                        direction={
                          directions[
                            index
                          ]
                        }
                        delay={
                          index *
                          0.045
                        }
                      >
                        <Spotlight className="eth-faq-v10-review-card">

                          <div className="eth-faq-v10-review-card-top">
                            <span>
                              0{
                                index +
                                1
                              }
                            </span>

                            <Icon
                              size={20}
                            />
                          </div>

                          <small>
                            {
                              card.label
                            }
                          </small>

                          <h3>
                            {
                              card.title
                            }
                          </h3>

                          <p>
                            {
                              card.body
                            }
                          </p>

                        </Spotlight>
                      </Reveal>
                    );
                  }
                )
              }

            </div>

          </div>
        </section>


        {/* ====================================================
            EXPLORER
        ==================================================== */}

        <section
          className="eth-faq-v10-section eth-faq-v10-section--panel"
          id="faq-explorer"
        >

          <div className="eth-faq-v10-shell">

            <Reveal
              direction="topRight"
              className="eth-faq-v10-section-head"
            >
              <span className="eth-faq-v10-kicker">
                SEARCH THE OPERATOR GUIDE
              </span>

              <h2>
                Start with a question. <span>Find the practical answer.</span>
              </h2>

              <p>
                Search by a specific concern or browse the ten topics. Each group ends with a short checklist of what to confirm in a demo, scope document, or operator agreement.
              </p>
            </Reveal>


            <Reveal
              direction="bottomLeft"
              className="eth-faq-v10-search-panel"
            >

              <div className="eth-faq-v10-search-box">

                <Search
                  size={18}
                />

                <input
                  type="search"
                  value={
                    query
                  }
                  onChange={
                    event =>
                      setQuery(
                        event.target.value
                      )
                  }
                  placeholder="Search demos, launch, games, payments, compliance, pricing..."
                  aria-label="Search Ethosino FAQ"
                />

                {
                  query
                    ? (
                      <button
                        type="button"
                        onClick={() =>
                          setQuery(
                            ""
                          )
                        }
                        aria-label="Clear search"
                      >
                        <X
                          size={16}
                        />
                      </button>
                    )
                    : null
                }

              </div>


              <div className="eth-faq-v10-search-meta">

                <span>
                  {
                    resultCount
                  }{" "}
                  answers found
                </span>

                <i />

                <span>
                  {
                    activeCategory === "all"
                      ? "all topics"
                      : faqCategories.find(
                          category =>
                            category.id ===
                            activeCategory
                        )?.label
                  }
                </span>

              </div>

            </Reveal>


            <div className="eth-faq-v10-explorer-layout">

              <Reveal
                direction="left"
                className="eth-faq-v10-category-rail"
              >

                <button
                  type="button"
                  className={[
                    "eth-faq-v10-category-button",
                    activeCategory === "all"
                      ? "is-active"
                      : ""
                  ].join(
                    " "
                  )}
                  onClick={() =>
                    setActiveCategory(
                      "all"
                    )
                  }
                >

                  <span className="eth-faq-v10-category-index">
                    00
                  </span>

                  <span className="eth-faq-v10-category-icon">
                    <CircleHelp
                      size={17}
                    />
                  </span>

                  <span className="eth-faq-v10-category-copy">

                    <small>
                      ALL TOPICS
                    </small>

                    <strong>
                      Browse every question
                    </strong>

                  </span>

                </button>


                {
                  faqCategories.map(
                    category => {
                      const Icon =
                        category.icon;

                      return (
                        <motion.button
                          type="button"
                          key={
                            category.id
                          }
                          className={[
                            "eth-faq-v10-category-button",
                            `eth-faq-v10-category-button--${category.accent}`,
                            activeCategory === category.id
                              ? "is-active"
                              : ""
                          ].join(
                            " "
                          )}
                          onClick={() =>
                            setActiveCategory(
                              category.id
                            )
                          }
                          whileHover={{
                            x:
                              5
                          }}
                          whileTap={{
                            scale:
                              0.985
                          }}
                        >

                          <span className="eth-faq-v10-category-index">
                            {
                              category.index
                            }
                          </span>

                          <span className="eth-faq-v10-category-icon">
                            <Icon
                              size={17}
                            />
                          </span>

                          <span className="eth-faq-v10-category-copy">

                            <small>
                              {
                                category.kicker
                              }
                            </small>

                            <strong>
                              {
                                category.label
                              }
                            </strong>

                          </span>

                        </motion.button>
                      );
                    }
                  )
                }

              </Reveal>


              <Reveal
                direction="right"
                className="eth-faq-v10-results"
              >

                <AnimatePresence
                  mode="popLayout"
                >

                  {
                    filteredGroups.length
                      ? filteredGroups.map(
                          (
                            category,
                            groupIndex
                          ) => {
                            const Icon =
                              category.icon;

                            return (
                              <motion.section
                                key={
                                  category.id
                                }
                                className={`eth-faq-v10-result-group eth-faq-v10-result-group--${category.accent}`}
                                layout={
                                  !reduceMotion
                                }
                                initial={
                                  reduceMotion
                                    ? false
                                    : {
                                        opacity:
                                          0,
                                        x:
                                          groupIndex %
                                          2 ===
                                          0
                                            ? 60
                                            : -60,
                                        y:
                                          20,
                                        filter:
                                          "blur(7px)"
                                      }
                                }
                                animate={{
                                  opacity:
                                    1,
                                  x:
                                    0,
                                  y:
                                    0,
                                  filter:
                                    "blur(0px)"
                                }}
                                exit={
                                  reduceMotion
                                    ? undefined
                                    : {
                                        opacity:
                                          0,
                                        y:
                                          18,
                                        filter:
                                          "blur(5px)"
                                      }
                                }
                                transition={{
                                  duration:
                                    0.33
                                }}
                              >

                                <header className="eth-faq-v10-result-head">

                                  <div className="eth-faq-v10-result-head-icon">
                                    <Icon
                                      size={21}
                                    />
                                  </div>

                                  <div>

                                    <small>
                                      {
                                        category.kicker
                                      }
                                    </small>

                                    <h3>
                                      {
                                        category.label
                                      }
                                    </h3>

                                    <p>
                                      {
                                        category.summary
                                      }
                                    </p>

                                  </div>

                                  <span className="eth-faq-v10-result-count">
                                    {
                                      category.questions.length
                                    }{" "}
                                    Q
                                  </span>

                                </header>


                                <div className="eth-faq-v10-question-list">

                                  {
                                    category.questions.map(
                                      (
                                        item,
                                        index
                                      ) => (
                                        <FAQItem
                                          key={
                                            item.id
                                          }
                                          item={
                                            item
                                          }
                                          category={
                                            category
                                          }
                                          index={
                                            index
                                          }
                                          isOpen={
                                            openQuestion ===
                                            item.id
                                          }
                                          onToggle={() =>
                                            setOpenQuestion(
                                              openQuestion === item.id
                                                ? ""
                                                : item.id
                                            )
                                          }
                                          reduceMotion={
                                            reduceMotion
                                          }
                                        />
                                      )
                                    )
                                  }

                                </div>


                                <div className="eth-faq-v10-deep-dive">

                                  <div className="eth-faq-v10-deep-dive-head">

                                    <Sparkles
                                      size={17}
                                    />

                                    <div>

                                      <span>
                                        WHAT TO CONFIRM
                                      </span>

                                      <strong>
                                        {
                                          category.deepDive.title
                                        }
                                      </strong>

                                    </div>

                                  </div>


                                  <p>
                                    {
                                      category.deepDive.body
                                    }
                                  </p>


                                  <div className="eth-faq-v10-deep-dive-points">

                                    {
                                      category.deepDive.bullets.map(
                                        item => (
                                          <span
                                            key={
                                              item
                                            }
                                          >
                                            <Check
                                              size={12}
                                            />

                                            {
                                              item
                                            }
                                          </span>
                                        )
                                      )
                                    }

                                  </div>

                                </div>

                              </motion.section>
                            );
                          }
                        )
                      : (
                        <motion.div
                          className="eth-faq-v10-empty"
                          initial={{
                            opacity:
                              0,
                            y:
                              15
                          }}
                          animate={{
                            opacity:
                              1,
                            y:
                              0
                          }}
                        >

                          <Search
                            size={26}
                          />

                          <h3>
                            No matches for that search.
                          </h3>

                          <p>
                            Try fewer words, a different topic, or show all questions.
                          </p>

                          <button
                            type="button"
                            onClick={() => {
                              setQuery(
                                ""
                              );
                              setActiveCategory(
                                "all"
                              );
                            }}
                          >
                            Reset filters
                          </button>

                        </motion.div>
                      )
                  }

                </AnimatePresence>

              </Reveal>

            </div>

          </div>
        </section>


        {/* ====================================================
            PROOF PATH
        ==================================================== */}

        <section
          className="eth-faq-v10-section"
          id="faq-proof"
        >

          <div className="eth-faq-v10-shell">

            <Reveal
              direction="topLeft"
              className="eth-faq-v10-section-head eth-faq-v10-section-head--split"
            >

              <div>
                <span className="eth-faq-v10-kicker">
                  FROM DISCOVERY TO DECISION
                </span>

                <h2>
                  A useful evaluation has <span>five concrete checkpoints.</span>
                </h2>
              </div>

              <p>
                Use these checkpoints to turn the FAQ into an actual buying process. Start with the operator model, test relevant software, agree on external dependencies, confirm acceptance criteria, and document the commercial scope.
              </p>

            </Reveal>


            <Reveal
              direction="bottomRight"
              className="eth-faq-v10-proof-path"
            >

              <div className="eth-faq-v10-proof-track" />

              {
                [
                  {
                    icon:
                      Fingerprint,
                    label:
                      "DISCOVER",
                    value:
                      "markets + audience"
                  },
                  {
                    icon:
                      Binary,
                    label:
                      "DEMO",
                    value:
                      "games + admin"
                  },
                  {
                    icon:
                      WalletCards,
                    label:
                      "SCOPE",
                    value:
                      "providers + costs"
                  },
                  {
                    icon:
                      Database,
                    label:
                      "VALIDATE",
                    value:
                      "test acceptance"
                  },
                  {
                    icon:
                      RefreshCw,
                    label:
                      "AGREE",
                    value:
                      "written commitments"
                  }
                ].map(
                  (
                    step,
                    index
                  ) => {
                    const Icon =
                      step.icon;

                    return (
                      <motion.div
                        key={
                          step.label
                        }
                        className="eth-faq-v10-proof-node"
                        initial={
                          reduceMotion
                            ? false
                            : {
                                opacity:
                                  0,
                                y:
                                  index %
                                  2 ===
                                  0
                                    ? 48
                                    : -48
                              }
                        }
                        whileInView={{
                          opacity:
                            1,
                          y:
                            0
                        }}
                        viewport={{
                          once:
                            true
                        }}
                        transition={{
                          duration:
                            0.5,
                          delay:
                            index *
                            0.07
                        }}
                      >

                        <div>
                          <Icon
                            size={20}
                          />
                        </div>

                        <span>
                          {
                            step.label
                          }
                        </span>

                        <strong>
                          {
                            step.value
                          }
                        </strong>

                      </motion.div>
                    );
                  }
                )
              }


              <motion.span
                className="eth-faq-v10-proof-packet"
                animate={
                  reduceMotion
                    ? {}
                    : {
                        left: [
                          "2%",
                          "96%"
                        ]
                      }
                }
                transition={{
                  duration:
                    6,
                  repeat:
                    Infinity,
                  ease:
                    "linear"
                }}
              />

            </Reveal>


            <Reveal
              direction="left"
              className="eth-faq-v10-diligence-prompt"
            >

              <div className="eth-faq-v10-diligence-prompt-icon">
                <Search
                  size={20}
                />
              </div>

              <div>

                <span>
                  A BETTER FIRST-CALL QUESTION
                </span>

                <strong>
                  “For my market and launch model, show what is ready to demonstrate, what requires an external provider, what we must supply, and what each item would cost.”
                </strong>

              </div>

              <button
                type="button"
                onClick={
                  copyDiligencePrompt
                }
              >
                <Copy
                  size={14}
                />

                {
                  copied
                    ? "Copied"
                    : "Copy question"
                }
              </button>

            </Reveal>

          </div>
        </section>


        {/* ====================================================
            DISCLOSURE
        ==================================================== */}

        <section
          className="eth-faq-v10-section eth-faq-v10-section--panel"
          id="faq-disclosure"
        >

          <div className="eth-faq-v10-shell">

            <Reveal
              direction="topRight"
              className="eth-faq-v10-section-head"
            >
              <span className="eth-faq-v10-kicker">
                PUBLIC INFORMATION VS. DILIGENCE
              </span>

              <h2>
                Know what can be answered here. <span>Request evidence for the rest.</span>
              </h2>

              <p>
                A public sales site can explain the business model, available workflows and known limitations. A specific launch requires written commercial scope, provider confirmation, test results and—where appropriate—controlled technical review.
              </p>
            </Reveal>


            <div className="eth-faq-v10-disclosure-grid">

              <Reveal
                direction="bottomLeft"
              >
                <Spotlight className="eth-faq-v10-disclosure-card eth-faq-v10-disclosure-card--public">

                  <div className="eth-faq-v10-disclosure-head">

                    <span>
                      <Eye
                        size={18}
                      />
                    </span>

                    <div>

                      <small>
                        PUBLIC / EXPLAINED
                      </small>

                      <h3>
                        What you can assess now.
                      </h3>

                    </div>

                  </div>


                  <div className="eth-faq-v10-disclosure-list">

                    {
                      [
                        "What Ethosino sells and who it serves",
                        "Demo entry and operator walkthrough options",
                        "Available game and admin capabilities",
                        "Published licensing components and examples",
                        "Operator and vendor responsibilities",
                        "Known limitations and separate requirements"
                      ].map(
                        item => (
                          <div
                            key={
                              item
                            }
                          >
                            <Check
                              size={12}
                            />

                            <p>
                              {
                                item
                              }
                            </p>
                          </div>
                        )
                      )
                    }

                  </div>

                </Spotlight>
              </Reveal>


              <Reveal
                direction="bottomRight"
              >
                <Spotlight className="eth-faq-v10-disclosure-card eth-faq-v10-disclosure-card--private">

                  <div className="eth-faq-v10-disclosure-head">

                    <span>
                      <LockKeyhole
                        size={18}
                      />
                    </span>

                    <div>

                      <small>
                        QUOTE / CONTROLLED REVIEW
                      </small>

                      <h3>
                        What needs specific evidence.
                      </h3>

                    </div>

                  </div>


                  <div className="eth-faq-v10-disclosure-list">

                    {
                      [
                        "Exact deployment scope and production readiness",
                        "Payment and identity-provider approval status",
                        "Committed uptime, support and incident targets",
                        "Security testing and sensitive architecture documents",
                        "Data rights, export and transition provisions",
                        "Final fees, revenue definitions and pilot conversion"
                      ].map(
                        item => (
                          <div
                            key={
                              item
                            }
                          >
                            <X
                              size={12}
                            />

                            <p>
                              {
                                item
                              }
                            </p>
                          </div>
                        )
                      )
                    }

                  </div>

                </Spotlight>
              </Reveal>

            </div>


            <Reveal
              direction="right"
              className="eth-faq-v10-disclosure-note"
            >

              <ShieldCheck
                size={18}
              />

              <div>

                <span>
                  HOW TO USE THE DIFFERENCE
                </span>

                <p>
                  Ask for the proof appropriate to the decision: use the public demo for product behavior, a statement of work for commitments, and a controlled review for sensitive technical evidence.
                </p>

              </div>

            </Reveal>

          </div>
        </section>


        {/* ====================================================
            OPERATOR / COMMERCIAL + LEGAL
        ==================================================== */}

        <section
          className="eth-faq-v10-section"
          id="faq-operator"
        >

          <div className="eth-faq-v10-shell">

            <div className="eth-faq-v10-operator-layout">

              <Reveal
                direction="left"
                className="eth-faq-v10-operator-copy"
              >
                <span className="eth-faq-v10-kicker">
                  WHO IS RESPONSIBLE
                </span>

                <h2>
                  Two parties contribute. <span>The responsibilities must be explicit.</span>
                </h2>

                <p>
                  Ethosino supplies the agreed software and implementation work. The operator brings the business model, player-acquisition plan, approved jurisdictions, legal and provider relationships, and accountable staff. Some tasks may be shared, but each should have a named owner.
                </p>

                <p>
                  Make the scope actionable: identify who funds and approves payments, who manages redemptions, who responds to customers, who reviews incidents, and who signs off before launch. Put the division of work into the proposal rather than relying on sales conversations.
                </p>


                <Link
                  to="/pricing"
                  className="eth-faq-v10-inline-link"
                >
                  Review the pricing model

                  <ArrowRight
                    size={15}
                  />
                </Link>
              </Reveal>


              <Reveal
                direction="topRight"
                className="eth-faq-v10-boundary-stack"
              >

                <div className="eth-faq-v10-boundary-card">

                  <div className="eth-faq-v10-boundary-card-head">
                    <Plug
                      size={19}
                    />

                    <div>
                      <span>
                        ETHOSINO / AGREED SCOPE
                      </span>

                      <strong>
                        Licensed software + delivery
                      </strong>
                    </div>
                  </div>

                  <div className="eth-faq-v10-boundary-items">

                    <span>
                      platform access + setup
                    </span>

                    <span>
                      contracted game package
                    </span>

                    <span>
                      wallet and admin workflows
                    </span>

                    <span>
                      defined integrations
                    </span>

                    <span>
                      technical delivery checks
                    </span>

                    <span>
                      agreed support scope
                    </span>

                  </div>

                </div>


                <div className="eth-faq-v10-boundary-divider">

                  <span>
                    BOUNDARY
                  </span>

                  <i />

                </div>


                <div className="eth-faq-v10-boundary-card eth-faq-v10-boundary-card--operator">

                  <div className="eth-faq-v10-boundary-card-head">
                    <Scale
                      size={19}
                    />

                    <div>
                      <span>
                        OPERATOR / BUSINESS SCOPE
                      </span>

                      <strong>
                        The operating business
                      </strong>
                    </div>
                  </div>

                  <div className="eth-faq-v10-boundary-items">

                    <span>
                      audience + acquisition
                    </span>

                    <span>
                      legal eligibility + terms
                    </span>

                    <span>
                      payment + KYC partners
                    </span>

                    <span>
                      player service + redemptions
                    </span>

                    <span>
                      operating policy + staffing
                    </span>

                    <span>
                      business sign-off
                    </span>

                  </div>

                </div>

              </Reveal>

            </div>

          </div>
        </section>


        {/* ====================================================
            FINAL
        ==================================================== */}

        <section className="eth-faq-v10-final">

          <div className="eth-faq-v10-shell">

            <Reveal
              direction="bottomLeft"
              className="eth-faq-v10-final-card"
            >

              <div className="eth-faq-v10-final-grid" />

              <div className="eth-faq-v10-final-beam" />


              <div className="eth-faq-v10-final-copy">

                <span>
                  GO FROM QUESTIONS TO A PLAN.
                </span>

                <h2>
                  Bring your launch requirements. <strong>Leave with a clearer scope.</strong>
                </h2>

                <p>
                  A walkthrough should connect the product to your launch model: games to be enabled, integrations to qualify, staff workflows to test, commercial terms to define, and the milestones that must pass before a real operator opens.
                </p>


                <div className="eth-faq-v10-final-actions">

                  <Link
                    to="/book-demo"
                    className="eth-faq-v10-btn eth-faq-v10-btn--primary eth-faq-v10-btn--large"
                  >
                    Discuss my operator launch

                    <ArrowRight
                      size={18}
                    />
                  </Link>


                  <Link
                    to="/request-documentation"
                    className="eth-faq-v10-btn eth-faq-v10-btn--ghost eth-faq-v10-btn--large"
                  >
                    Request technical scope
                  </Link>

                </div>

              </div>


              <div className="eth-faq-v10-final-checklist">

                <div className="eth-faq-v10-final-checklist-head">

                  <ShieldCheck
                    size={21}
                  />

                  <div>
                    <span>
                      BRING TO THE CALL
                    </span>

                    <strong>
                      Make the conversation specific.
                    </strong>
                  </div>

                </div>


                <div className="eth-faq-v10-final-checklist-list">

                  <span>
                    <Check
                      size={13}
                    />
                    Your intended model and target markets
                  </span>

                  <span>
                    <Check
                      size={13}
                    />
                    Game catalog and player audience
                  </span>

                  <span>
                    <Check
                      size={13}
                    />
                    Payments, identity and provider assumptions
                  </span>

                  <span>
                    <Check
                      size={13}
                    />
                    Launch timeframe and team responsibilities
                  </span>

                  <span>
                    <Check
                      size={13}
                    />
                    Budget, pilot goals and decision criteria
                  </span>

                </div>

              </div>

            </Reveal>

          </div>
        </section>

      </main>


      <Footer />

    </Fragment>
  );
};


export default OperatorFAQHelperPage;
